import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import { 
  DocumentArrowUpIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MapPinIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const DataImport = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<any>(null)

  const { data: importStats, refetch: refetchStats } = useQuery({
    queryKey: ['importStats'],
    queryFn: () => api.getImportStats().then(res => res.data)
  })

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      
      return api.uploadCSV(formData).then(res => res.data)
    },
    onSuccess: (data) => {
      setUploadResult(data)
      refetchStats()
      toast.success(`Successfully imported ${data.processed_orders} orders!`)
    },
    onError: (error: any) => {
      toast.error(`Upload failed: ${error.response?.data?.detail || error.message}`)
    }
  })

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setUploadResult(null)
    }
  }

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <h1 className="text-2xl font-bold">Data Import Center</h1>
        <p className="text-green-100 mt-1">Upload 7tabz distribution data from CSV files</p>
      </div>

      {/* Current Database Stats */}
      {importStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{importStats.overview.total_orders.toLocaleString()}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(importStats.overview.total_revenue)}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Quantity</p>
                <p className="text-2xl font-bold text-gray-900">{importStats.overview.total_quantity.toLocaleString()}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">States Covered</p>
                <p className="text-2xl font-bold text-gray-900">{importStats.states?.length || 0}</p>
              </div>
              <MapPinIcon className="h-8 w-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload New CSV Data</h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          
          {!selectedFile ? (
            <div>
              <p className="text-lg font-medium text-gray-900 mb-2">Select CSV File</p>
              <p className="text-sm text-gray-600 mb-4">
                Upload your 7tabz distribution data CSV file
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
              >
                Choose File
              </label>
            </div>
          ) : (
            <div>
              <CheckCircleIcon className="mx-auto h-8 w-8 text-green-500 mb-2" />
              <p className="text-lg font-medium text-gray-900 mb-2">{selectedFile.name}</p>
              <p className="text-sm text-gray-600 mb-4">
                File size: {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
              <div className="space-x-4">
                <button
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload & Process'}
                </button>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Choose Different File
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Expected Format Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">Expected CSV Format</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your CSV should have columns: Date, Distributor Name, State, ZIP, Product Name, Unit Price, Quantity
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Example: 5/24/2024, "Trans Wireless", TX, 75229, "7Tabz | 7-Hydroxy Tablet (Pack of 4) | 15mg", 11.75, 600
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Results</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-900">Processed</p>
                  <p className="text-lg font-semibold text-green-900">{uploadResult.processed_orders}</p>
                </div>
              </div>
            </div>
            
            {uploadResult.skipped_rows > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Skipped</p>
                    <p className="text-lg font-semibold text-yellow-900">{uploadResult.skipped_rows}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Total Rows</p>
                  <p className="text-lg font-semibold text-blue-900">{uploadResult.total_rows}</p>
                </div>
              </div>
            </div>
          </div>

          {uploadResult.skipped_details && uploadResult.skipped_details.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-2">Skipped Rows Details:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {uploadResult.skipped_details.map((detail: string, index: number) => (
                  <li key={index}>• {detail}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Product & State Breakdown */}
      {importStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Breakdown */}
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Breakdown</h3>
            <div className="space-y-3">
              {importStats.products?.map((product: any) => (
                <div key={product.category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{product.category}</p>
                    <p className="text-sm text-gray-600">{product.orders} orders • {product.quantity} units</p>
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top States */}
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top States by Revenue</h3>
            <div className="space-y-3">
              {importStats.states?.slice(0, 10).map((state: any) => (
                <div key={state.state} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">{state.state}</p>
                      <p className="text-sm text-gray-600">{state.orders} orders</p>
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(state.revenue)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataImport
